import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Save, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, notifyError, imageUrl } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  title: "",
  description: "",
  image_url: "",
  image_url_mobile: "",
  cta_text: "",
  cta_link: "",
  is_active: true,
  start_date: "",
  end_date: "",
  priority: 0,
  locations: ["homepage"],
  frequency: "once_per_day",
  target_audience: "all",
};

const LOCATIONS = ["homepage", "after_login", "checkout"];
const FREQUENCIES = [
  { value: "every_visit", label: "Setiap kunjungan" },
  { value: "once_per_day", label: "1x per hari" },
  { value: "once_per_user", label: "1x saja per customer" },
];
const AUDIENCES = [
  { value: "all", label: "Semua customer" },
  { value: "new_customer", label: "Customer baru (belum pernah order)" },
  { value: "returning_customer", label: "Customer yang sudah pernah order" },
];

export default function PopupsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/popups");
      setItems(data || []);
    } catch (e) { notifyError(e); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p, locations: p.locations || ["homepage"] }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const upload = async (file, field = "image_url") => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Hanya JPG/PNG/WEBP"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Maksimal 2MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.path || data.url }));
      toast.success("Gambar diupload");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    if (form.locations.length === 0) { toast.error("Pilih minimal 1 lokasi"); return; }
    setBusy(true);
    try {
      const payload = { ...form, priority: Number(form.priority) || 0 };
      if (editing === "new") await api.post("/admin/popups", payload);
      else await api.put(`/admin/popups/${editing}`, payload);
      toast.success("Popup disimpan");
      cancel();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus popup "${p.title}"?`)) return;
    try { await api.delete(`/admin/popups/${p.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleLoc = (loc) => {
    setForm((f) => ({
      ...f,
      locations: f.locations.includes(loc) ? f.locations.filter((x) => x !== loc) : [...f.locations, loc],
    }));
  };

  return (
    <AdminLayout title="Popup Banner Promo">
      <div className="max-w-3xl space-y-5" data-testid="admin-popups-page">
        <div className="flex items-center justify-between">
          <div className="text-sm text-[#5C5C5C]">Buat popup promo yang muncul di halaman customer (homepage, setelah login, checkout).</div>
          <Button onClick={startNew} disabled={editing === "new"} className="bg-[#4A2E1F] text-white rounded-xl" data-testid="add-popup-btn">
            <Plus size={16} className="mr-1" /> Tambah Popup
          </Button>
        </div>

        {editing && (
          <Card className="p-5 space-y-3" data-testid="popup-form">
            <h3 className="font-semibold text-sm">{editing === "new" ? "Popup Baru" : "Edit Popup"}</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Judul *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="popup-title-input" /></div>
              <div className="md:col-span-2"><Label>Deskripsi</Label>
                <textarea className="w-full border border-[#E5E5E5] rounded-lg p-2 text-sm" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="popup-desc-input" />
              </div>
              <div className="md:col-span-2">
                <Label>Gambar Desktop (JPG/PNG/WEBP, max 2MB) — rasio 16:9 dianjurkan</Label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(e.target.files?.[0], "image_url")} className="text-xs" data-testid="popup-image-input" />
                  {uploading && <span className="text-xs">Upload...</span>}
                  {form.image_url && <img src={imageUrl(form.image_url)} alt="" className="w-16 h-16 object-cover rounded border" />}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Gambar Mobile (opsional — rasio 1:1/4:5 dianjurkan)</Label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => upload(e.target.files?.[0], "image_url_mobile")} className="text-xs" data-testid="popup-image-mobile-input" />
                  {form.image_url_mobile && <img src={imageUrl(form.image_url_mobile)} alt="" className="w-16 h-16 object-cover rounded border" />}
                  {form.image_url_mobile && (
                    <Button size="sm" variant="ghost" type="button" onClick={() => setForm((f) => ({ ...f, image_url_mobile: "" }))} className="text-xs h-7 px-2" data-testid="popup-image-mobile-clear">Hapus</Button>
                  )}
                </div>
                <p className="text-[10px] text-[#5C5C5C] mt-1">Kalau kosong, gambar desktop dipakai untuk semua device.</p>
              </div>
              <div><Label>CTA Text</Label><Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="Belanja Sekarang" data-testid="popup-cta-text" /></div>
              <div><Label>CTA Link</Label><Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="/produk atau https://..." data-testid="popup-cta-link" /></div>
              <div><Label>Tanggal Mulai (ISO)</Label><Input value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} placeholder="2026-06-01T00:00:00Z" data-testid="popup-start-date" /></div>
              <div><Label>Tanggal Selesai (ISO)</Label><Input value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} placeholder="2026-12-31T23:59:59Z" data-testid="popup-end-date" /></div>
              <div><Label>Prioritas (higher = tampil duluan)</Label><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="popup-priority" /></div>
              <div>
                <Label>Frekuensi</Label>
                <select className="w-full border border-[#E5E5E5] rounded-lg p-2 text-sm" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="popup-frequency">
                  {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Target Customer</Label>
                <select className="w-full border border-[#E5E5E5] rounded-lg p-2 text-sm" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} data-testid="popup-target-audience">
                  {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Lokasi Tampil</Label>
                <div className="flex flex-wrap gap-3 mt-1">
                  {LOCATIONS.map((loc) => (
                    <label key={loc} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                      <input type="checkbox" checked={form.locations.includes(loc)} onChange={() => toggleLoc(loc)} data-testid={`popup-loc-${loc}`} />
                      {loc.replace("_", " ")}
                    </label>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="popup-active-toggle" />
                <span className="text-sm">{form.is_active ? "Aktif" : "Nonaktif"}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy} className="bg-[#4A2E1F] text-white rounded-xl" data-testid="popup-save-btn">
                <Save size={16} className="mr-1" /> {busy ? "Menyimpan..." : "Simpan"}
              </Button>
              <Button onClick={cancel} variant="outline" className="rounded-xl"><X size={16} className="mr-1" /> Batal</Button>
            </div>
          </Card>
        )}

        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF9F6] border-b">
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold">
                <th className="py-3 px-4">Gambar</th>
                <th className="py-3 px-4">Judul</th>
                <th className="py-3 px-4">Lokasi</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-center">Prioritas</th>
                <th className="py-3 px-4 w-24">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-[#FAF9F6]">
                  <td className="py-3 px-4">{p.image_url ? <img src={imageUrl(p.image_url)} alt="" className="w-12 h-12 object-cover rounded" /> : <ImageIcon size={20} className="text-[#5C5C5C]" />}</td>
                  <td className="py-3 px-4 font-medium">{p.title}</td>
                  <td className="py-3 px-4 text-xs">{(p.locations || []).join(", ")}</td>
                  <td className="py-3 px-4"><span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{p.is_active ? "Aktif" : "Nonaktif"}</span></td>
                  <td className="py-3 px-4 text-center">{p.priority}</td>
                  <td className="py-3 px-4"><div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => startEdit(p)}><Edit2 size={14} /></Button><Button size="sm" variant="ghost" onClick={() => remove(p)}><Trash2 size={14} className="text-red-600" /></Button></div></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-xs text-[#5C5C5C]">Belum ada popup. Klik "Tambah Popup".</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </AdminLayout>
  );
}
